import { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, Button, addToast } from '@heroui/react';
import { FaCalendarAlt } from 'react-icons/fa';
import googleCalendarService, {
  GoogleCalendarStatus,
} from '../../../../service/googleCalendarService';
import PageHeader from '../../../components/common/ui/PageHeader';
import CustomChip from '../../../components/CustomChip';
import { useAuth } from '../../../../context/AuthContext';

interface AppConnection {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  connected: boolean;
}

const initialApps: AppConnection[] = [
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Sync your bookings and reminders with Google Calendar.',
    icon: <FaCalendarAlt className="text-[#4285F4]" />,
    connected: false,
  },
  // {
  //   id: 'notes',
  //   name: 'Notes',
  //   description: 'Save and sync personal notes within your account.',
  //   icon: <FaRegStickyNote className="text-[#16a34a]" />,
  //   connected: false,
  // },
  // {
  //   id: 'facebook',
  //   name: 'Facebook',
  //   description: 'Connect Facebook to enhance your social experience.',
  //   icon: <FaFacebook className="text-[#1877F2]" />,
  //   connected: false,
  // },
];

const ConnectedApps = () => {
  const { hasPermissions } = useAuth();
  const canAccess = hasPermissions();
  const [apps, setApps] = useState<AppConnection[]>(initialApps);
  const [busy, setBusy] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState<string | null>(null);
  const [calendarStatus, setCalendarStatus] =
    useState<GoogleCalendarStatus | null>(null);

  useEffect(() => {
    loadCalendarStatus();
    // handle OAuth query params
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');
    if (success === 'true') {
      addToast({
        title: 'Connected',
        description: 'Google Calendar connected successfully',
        color: 'success',
        radius: 'md',
      });
      // refresh status
      loadCalendarStatus();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      addToast({
        title: 'Connection failed',
        description: 'Could not connect to Google Calendar.',
        color: 'danger',
        radius: 'md',
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadCalendarStatus = async () => {
    try {
      const status = await googleCalendarService.getStatus();
      // backend returns { success, isConnected, email, lastSyncedAt }
      setCalendarStatus(status);
      setApps((prev) =>
        prev.map((a) =>
          a.id === 'google-calendar'
            ? { ...a, connected: !!status.isConnected }
            : a
        )
      );
    } catch (err) {
      // ignore silently
      console.error('Failed to load calendar status', err);
    }
  };

  const toggleConnection = async (id: string) => {
    if (id !== 'google-calendar') return;
    try {
      setBusy(id);
      const current = apps.find((a) => a.id === id);
      const willConnect = !current?.connected;
      if (willConnect) {
        const { authUrl } = await googleCalendarService.getAuthUrl();
        // redirect to Google consent
        window.location.href = authUrl;
      } else {
        await googleCalendarService.disconnect();
        setApps((prev) =>
          prev.map((a) => (a.id === id ? { ...a, connected: false } : a))
        );
        setCalendarStatus(null);
        addToast({
          title: 'Disconnected',
          description: 'Google Calendar disconnected successfully',
          color: 'warning',
          radius: 'md',
        });
      }
    } catch (e: any) {
      addToast({
        title: 'Action failed',
        description:
          e?.response?.data?.message ||
          'Could not update connection. Please try again.',
        color: 'danger',
        radius: 'md',
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Connected Apps"
        description="Manage and control which external apps are connected to your account."
      />

      {/* Keep cards sizing/position consistent with the previous Connected Apps layout */}
      <div className="w-full max-w-6xl xl:max-w-screen-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {apps.map((app) => (
            <Card
              key={app.id}
              radius="sm"
              className="border border-secondary/30 shadow-sm"
            >
              <CardHeader className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{app.icon}</span>
                  <div>
                    <p className="text-body-bold">{app.name}</p>
                    <p className="text-xs text-gray-500">{app.description}</p>
                    {app.id === 'google-calendar' && calendarStatus?.email && (
                      <p className="text-xs text-gray-400 mt-1">
                        Connected as: {calendarStatus.email}
                      </p>
                    )}
                  </div>
                </div>
                <CustomChip
                  label={app.connected ? 'Connected' : 'Not connected'}
                  color={app.connected ? 'success' : 'default'}
                  variant={app.connected ? 'flat' : 'bordered'}
                  size="sm"
                />
              </CardHeader>
              <CardBody className="flex justify-end gap-2">
                {app.id === 'google-calendar' && app.connected && (
                  <Button
                    size="sm"
                    color="secondary"
                    variant="flat"
                    onPress={async () => {
                      try {
                        setSyncBusy(app.id);
                        const res = await googleCalendarService.syncBookings();
                        // Backend may return either { synced } or a background "started" message.
                        if (res && typeof (res as any).synced === 'number') {
                          addToast({
                            title: 'Synced',
                            description: `Synced ${(res as any).synced} booking(s) to Google Calendar`,
                            color: 'success',
                            radius: 'md',
                          });
                        } else if (res && (res as any).message) {
                          addToast({
                            title: 'Sync started',
                            description: (res as any).message,
                            color: 'success',
                            radius: 'md',
                          });
                        } else {
                          addToast({
                            title: 'Sync requested',
                            description:
                              'Bookings are being synced in background',
                            color: 'success',
                            radius: 'md',
                          });
                        }
                      } catch (error: any) {
                        console.error('Sync error', error);
                        const resp = error?.response?.data;
                        const msg =
                          resp?.message ||
                          resp?.error?.message ||
                          resp?.error?.errors?.[0]?.message;

                        if (msg) {
                          addToast({
                            title: 'Sync Failed',
                            description: msg,
                            color: 'danger',
                            radius: 'md',
                          });
                        } else {
                          // Treat timeout/long-running errors as "sync started" as well
                          addToast({
                            title: 'Sync Started',
                            description:
                              'Sync is in progress. If it fails, check the sync status later.',
                            color: 'warning',
                            radius: 'md',
                          });
                        }
                      } finally {
                        setSyncBusy(null);
                      }
                    }}
                    isDisabled={busy === app.id || syncBusy === app.id}
                    isLoading={syncBusy === app.id}
                  >
                    Sync Now
                  </Button>
                )}
                {canAccess && (
                  <Button
                    size="sm"
                    color={app.connected ? 'danger' : 'primary'}
                    variant={app.connected ? 'flat' : 'solid'}
                    isDisabled={busy === app.id || syncBusy === app.id}
                    isLoading={busy === app.id}
                    onPress={() => toggleConnection(app.id)}
                  >
                    {app.connected ? 'Disconnect' : 'Connect'}
                  </Button>
                )}
              </CardBody>
            </Card>
          ))}
        </div>

        <div className="text-[11px] text-gray-500 mt-6">
          Note: Connecting an app may redirect you to the provider to authorize
          access.
        </div>
      </div>
    </div>
  );
};

export default ConnectedApps;
